import type { Delta, Op } from 'quill/core';
import React from 'react';
import ActivityItem from '../../activities/ActivityItem';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import commentIcon from '../../icons/non-prefilled/16x16/comment.svg';
import commentResolvedIcon from '../../icons/non-prefilled/16x16/commentResolved.svg';
import replyIcon from '../../icons/non-prefilled/16x16/reply.svg';
import CommentPreview from '../comment/CommentPreview';
import CommentPreviewText from '../comment/CommentPreviewText';
import * as styles from './CommentActivityItem.module.scss';

const COMMENT_ACTIVITY_ITEM_ACTORS_FRAGMENT = gql(`
  fragment CommentActivityItem_Actors on User {
    id
    ...ActivityItem_Actors
  }
`);

const MAX_COMMENT_LINE_COUNT = 2;

const icons = {
  [PostActivityAction.CommentCreate]: (
    <SVGIcon className={styles.commentIcon}>{commentIcon}</SVGIcon>
  ),
  [PostActivityAction.CommentReply]: (
    <SVGIcon className={styles.commentIcon}>{replyIcon}</SVGIcon>
  ),
  [PostActivityAction.CommentResolve]: (
    <SVGIcon className={styles.resolveIcon}>{commentResolvedIcon}</SVGIcon>
  ),
} as const;

export const isCommentActivity = (
  type: PostActivityAction,
): type is keyof typeof icons => Object.hasOwn(icons, type);

interface CommentActivityItemProps {
  type: keyof typeof icons;
  timestamp: string | number;
  actors: DocumentType<typeof COMMENT_ACTIVITY_ITEM_ACTORS_FRAGMENT>[];
  content: Delta | Op[];
  context: Delta | Op[];
  href?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const CommentActivityItem = ({
  type,
  timestamp,
  actors,
  context,
  content,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: CommentActivityItemProps) => {
  return (
    <ActivityItem
      icon={icons[type]}
      description={
        <>
          <span className={styles.commentContext}>
            <CommentPreviewText
              context={context}
              quoted={type === PostActivityAction.CommentReply}
              maxLines={1}
            />
          </span>
          <CommentPreview
            collapseLines
            content={content}
            link={false}
            maxLines={MAX_COMMENT_LINE_COUNT}
          />
        </>
      }
      timestamp={timestamp}
      actors={actors}
      href={href}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
};

export default CommentActivityItem;
